import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {resetTransformAction, setCurrentPageNumberAction} from '../../search/duck/search-actions';
import {useStateNumberOfPages} from '../../search/search-hooks';
import classNames from 'clsx';
import {useLocalizationUtil} from '../../localizationUtil';

type PdfPageNumberControllerProps = { pageNumber: number };

export const PdfPageInput = React.memo(({pageNumber}: PdfPageNumberControllerProps) => {
    const AVERAGE_CHAR_WIDTH = 10;
    const numberOfPages = useStateNumberOfPages();
    const dispatch = useDispatch();
    const {formatNumber} = useLocalizationUtil();

    // `inputElementNumber` allows flexible input (e.g., empty or exceeding pages)
    // while keeping actual `pageNumber` state consistent.
    const [inputElementNumber, setInputElementNumber] = useState<number | ''>(pageNumber);

    useEffect(() => {
        setInputElementNumber(pageNumber);
    }, [pageNumber]);

    const handlePageChange = (e: ChangeEvent<HTMLInputElement>) => {
        let newPage: number | '' = Number.parseInt(e.target.value);
        if (newPage > numberOfPages) {
            newPage = numberOfPages;
        } else if (isNaN(newPage) || newPage < 1) {
            newPage = '';
        }
        setInputElementNumber(newPage);
    };

    const handleLoseFocus = () => {
        setInputElementNumber(pageNumber);
    };

    const handleKeyDown = (pressedKey: string) => {
        if (pressedKey === 'Enter') {
            dispatch(resetTransformAction(true));
            if (inputElementNumber !== '') {
                dispatch(setCurrentPageNumberAction(inputElementNumber));
            }
        }
    };

    const getInputWidth = useCallback((value: number | '') => {
        return `${AVERAGE_CHAR_WIDTH * (value === '' || value === 0 ? 1 : value.toString().length)}px`;
    }, []);

    return (
        <p className={'toolbar-setting-text'}>
            <label className={classNames('label-as-text', (numberOfPages !== 1 ? 'active' : ''))}>
                <input
                    className={'input-as-text'}
                    type={'text'}
                    pattern={'[1-9]*'}
                    value={formatNumber(inputElementNumber)}
                    onKeyDown={(e) => handleKeyDown(e.key)}
                    onChange={(e) => handlePageChange(e)}
                    min={'1'}
                    max={numberOfPages}
                    onBlur={() => handleLoseFocus()}
                    style={{width: getInputWidth(inputElementNumber)}}
                    disabled={numberOfPages === 1}
                />
            </label>
            <span>/</span>
            <span>{formatNumber(numberOfPages)}</span>
        </p>
    );
});
